import { type AsyncListLoadOptions } from "@react-stately/data";
import debounce from "awesome-debounce-promise";
import pluralize from "pluralize";
import { type FC, type Key, useCallback, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { Item, Section, useAsyncList } from "react-stately";
import { titleCase } from "title-case";
import { useDebounce } from "use-debounce";
import { HStack, MenuOptionItem, SearchAutosuggest, Tag, TagIntent, Tooltip, TooltipIntent, useTooltip } from "widgets";

import { escapeRegexCharacters } from "../../../app-utils/string.js";
import { type InputSearchEntityTypeInput } from "../../../graphql.js";
import { zoomToRegion } from "../../../redux/modules/genome-browser.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import { Hotkeys } from "../../../settings/hotkeys.js";
import { FEATURE_DISPLAY_NAME } from "../../../settings/settings.js";
import {
  type FeatureSearchCategory,
  type FeatureSearchLazyResult,
  useFeatureSearchLazy,
} from "./useGetFeatureSearchGraphql.js";

export interface FeatureSearchProps {
  size: string; // responsive design
  organismId: number;
  isVirtualGenome: boolean;
  types: InputSearchEntityTypeInput[];
}

type Category = FeatureSearchLazyResult & {
  title?: string;
  items: NonNullable<FeatureSearchCategory>[];
};

export const FeatureSearch: FC<FeatureSearchProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const [search] = useFeatureSearchLazy();

  const debouncedLoad = useMemo(
    () =>
      debounce(async function load({ filterText }: AsyncListLoadOptions<Category, string>) {
        if (filterText == undefined || filterText.trim() === "" || filterText.trim().length < 3) return { items: [] };

        const response = await search({
          searchTerm: filterText,
          genomeId: props.organismId,
          types: props.types,
        });
        const searchResponse = response?.data?.search?.globalSearch;

        if (
          searchResponse &&
          filterText === searchResponse.searchTerm &&
          Array.isArray(searchResponse.categories) &&
          searchResponse.categories.length > 0
        ) {
          return {
            items: [
              ...searchResponse.categories.map((category: NonNullable<FeatureSearchLazyResult>) => {
                const sectionName = titleCase(FEATURE_DISPLAY_NAME[category.categoryType!]);

                return {
                  ...category,
                  title: category.categoryName ?? pluralize(sectionName, category.totalItemCount ?? 0),
                  items: category.items ?? [],
                };
              }),
            ] as Category[],
          };
        }

        return filterText
          ? {
              items: [
                {
                  totalItemCount: null,
                  categoryType: "Search",
                  categoryName: null,
                  subTypeId: "__global__",
                  items: [
                    {
                      id: "__nothing-found__",
                      name: "No results",
                      description: null,
                      type: null,
                      subTypeId: null,
                      region: null,
                    },
                  ],
                },
              ] satisfies Category[],
            }
          : { items: [] };
      }, 300),
    [props.organismId, props.types],
  );

  const list = useAsyncList<Category>({
    load: debouncedLoad,
  });

  const onSelectionChange = useCallback(
    (key: Key) => {
      if (key !== "unknown") {
        let res;

        for (const item of list.items) {
          for (const entry of item.items) {
            if (String(entry.id) === String(key)) {
              res = entry;
              break;
            }
          }
        }

        if (res == undefined) {
          return;
        }

        // zoom to selected item in its feature track
        dispatch(zoomToRegion(res.region));

        list.setFilterText(""); // force to close the menu...
      }
    },
    [dispatch, list],
  );

  const [isOpen] = useDebounce(!!list.filterText && list.filterText.length < 3, 700);
  const triggerRef = useRef<HTMLInputElement>(null);
  const tooltipRef = useRef(null);
  const { state, tooltipProps, triggerProps, arrowProps } = useTooltip(triggerRef, tooltipRef, {
    trigger: "focus",
    isOpen,
    shouldFlip: false,
    delay: 3000,
    offset: 5,
  });

  return (
    <>
      <SearchAutosuggest
        placeholder={"Feature search"}
        items={list.items}
        allowsCustomValue
        inputValue={list.filterText}
        onInputChange={list.setFilterText}
        isLoading={list.isLoading}
        selectedKey={null} // Nothing is ever selected
        width={180}
        icon={"search"}
        allowsEmptyCollection
        menuTrigger={"focus"}
        onSelectionChange={onSelectionChange}
        focusShortcut={Hotkeys.genomeViewer.search}
        ref={triggerRef}
        domProps={{ "aria-describedby": triggerProps["aria-describedby"] }}
      >
        {(category: Category) => (
          <Section
            key={`${category?.categoryType}_${category?.subTypeId}`}
            title={
              category?.title ? (
                <HStack>
                  {category?.title}
                  <Tag
                    intentColor={TagIntent.Outlined}
                    style={{ marginLeft: "auto" }}
                    label={category.totalItemCount}
                  />
                </HStack>
              ) : undefined
            }
            items={category?.items ?? []}
            aria-label={category?.title}
          >
            {(item) => {
              const contig = item?.region?.contig;
              return (
                <Item key={item.id} textValue={item.name!}>
                  <MenuOptionItem
                    title={
                      <span
                        dangerouslySetInnerHTML={{
                          __html:
                            item.name?.replace(
                              new RegExp("(" + escapeRegexCharacters(list.filterText) + ")", "gi"),
                              "<strong>$1</strong>",
                            ) ?? "",
                        }}
                      />
                    }
                    description={
                      <span>
                        {contig?.sequenceId}
                        {props.isVirtualGenome && contig ? ` (${contig.organism.name})` : ""}
                      </span>
                    }
                  />
                </Item>
              );
            }}
          </Section>
        )}
      </SearchAutosuggest>
      {state.isOpen && (
        <Tooltip
          domProps={tooltipProps}
          arrowProps={arrowProps}
          ref={tooltipRef}
          content={"Every search term should be at least 3 characters."}
          intent={TooltipIntent.Info}
        />
      )}
    </>
  );
};
